import React, { useEffect, useState } from 'react';
import { Alert, ListGroup, Modal, Button } from 'react-bootstrap';
import type { TrendingVideo } from '../../../../../infrastructure/TrendingVideoClient';

interface Props {
  show: boolean;
  onHide: () => void;
  videos: TrendingVideo[];
  onSave: (orderUpdates: Array<{ videoId: string; order: number }>) => Promise<void> | void;
  isLoading: boolean;
}

interface OrderedVideo extends TrendingVideo {
  tempOrder: number;
}

export default function TrendingVideoOrderModal({ show, onHide, videos, onSave, isLoading }: Props) {
  const [ordered, setOrdered] = useState<OrderedVideo[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (show && videos.length > 0) {
      const sorted = [...videos]
        .sort((a, b) => a.order - b.order)
        .map((video, index) => ({ ...video, tempOrder: index + 1 }));
      setOrdered(sorted);
    }
  }, [show, videos]);

  const reindex = (arr: OrderedVideo[]): OrderedVideo[] =>
    arr.map((video, index) => ({ ...video, tempOrder: index + 1 }));

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }
    const next = [...ordered];
    const [dragged] = next.splice(draggedIndex, 1);
    if (dragged) {
      next.splice(dropIndex, 0, dragged);
    }
    setOrdered(reindex(next));
    setDraggedIndex(null);
  };

  const moveItem = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= ordered.length) return;
    const next = [...ordered];
    const [movedItem] = next.splice(fromIndex, 1);
    if (movedItem) {
      next.splice(toIndex, 0, movedItem);
    }
    setOrdered(reindex(next));
  };

  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaveError(null);
    try {
      const updates = ordered.map((video) => ({ videoId: video.videoId, order: video.tempOrder }));
      await onSave(updates);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save order');
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Manage Trending Video Order</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="mb-3">
          Drag videos by the grip handle, or use the up/down buttons, to reorder. Order 1 appears first in the homepage carousel.
        </Alert>
        {saveError && (
          <Alert variant="danger" className="mb-3">
            {saveError}
          </Alert>
        )}

        {ordered.length === 0 ? (
          <div className="text-center py-4 text-muted">No videos to order</div>
        ) : (
          <ListGroup>
            {ordered.map((video, index) => (
              <ListGroup.Item
                key={video.videoId}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                className={`d-flex align-items-center ${draggedIndex === index ? 'opacity-50' : ''}`}
                style={{ cursor: 'grab' }}
              >
                <span
                  className="text-muted me-3 d-flex align-items-center"
                  style={{ fontSize: '1.25rem', lineHeight: 1, userSelect: 'none' }}
                  title="Drag to reorder"
                >
                  ☰
                </span>
                <span className="badge bg-primary fs-6 me-3">#{video.tempOrder}</span>
                <video
                  src={video.videoUrl}
                  preload="metadata"
                  muted
                  style={{ width: '80px', height: '45px', objectFit: 'cover' }}
                  className="rounded me-3"
                />
                <div className="flex-grow-1">
                  <div className="fw-bold">{video.productFocus}</div>
                  <div className="text-muted small">
                    {video.productSlug} • {video.isVisible ? 'Visible' : 'Hidden'}
                  </div>
                </div>
                <div className="d-flex flex-column gap-1 ms-3">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => moveItem(index, index - 1)}
                    disabled={index === 0}
                    aria-label="Move up"
                    title="Move up"
                  >
                    ▲
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => moveItem(index, index + 1)}
                    disabled={index === ordered.length - 1}
                    aria-label="Move down"
                    title="Move down"
                  >
                    ▼
                  </Button>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isLoading || ordered.length === 0}>
          {isLoading ? 'Saving…' : 'Save Order'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
